var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useEffect, useState } from 'react';
import { joinTimeRanges } from '../helpers/helpers';
import { DEFAULT_PLAYBACK_SPEED, ERROR_MARGE_FOR_VIEW_RANGE, FRAME_INTERVAL, PLAYBACK_SPEEDS } from '../constants';
var useEffectOnExistingElement = function (func, element, deps) {
    useEffect(function () {
        if (element) {
            func();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [element, func, deps]);
};
var getTimeViewed = function (ranges) {
    var time = 0;
    ranges.forEach(function (range) { return (time += range.end - range.start); });
    return time;
};
// OBS: we could put PERCENT_FOR_ANSSI_VALIDATION outside TimeViewedValidation.ts, would be better for performance
// because we can stop update timeViewed when timeViewed > PERCENT_FOR_ANSSI_VALIDATION is verified,
// but we loose a bit atomic
export var useVideo = function (video) {
    var _a, _b;
    var _c = useState(false), isPlaying = _c[0], setIsPlaying = _c[1];
    var _d = useState(false), isEnded = _d[0], setIsEnded = _d[1];
    var _e = useState(0), downloadRate = _e[0], setDownloadRate = _e[1];
    var _f = useState(0), timeRate = _f[0], setTimeRate = _f[1];
    var _g = useState(DEFAULT_PLAYBACK_SPEED), speed = _g[0], setSpeed = _g[1];
    var _h = useState(0), startRangePlayed = _h[0], setStartRangePlayed = _h[1];
    var _j = useState([]), videoPlayedRange = _j[0], setVideoPlayedRange = _j[1];
    var _k = useState(0), timeViewed = _k[0], setTimeViewed = _k[1];
    // handle Video.current time without onTimeUpdateCallback cause ui refresh is too long
    useEffect(function () {
        if (video.current && isPlaying) {
            var readTime = function () {
                var _a;
                if (((_a = video.current) === null || _a === void 0 ? void 0 : _a.duration) && !Number.isNaN(video.current.duration) && video.current.duration > 0) {
                    var timeRate_1 = (video.current.currentTime / video.current.duration) * 100; // will get you a lot more updates.
                    setTimeRate(timeRate_1);
                }
            };
            var checkTimeViewed = function () {
                var _a;
                var currentRange = {
                    start: startRangePlayed,
                    end: (_a = video === null || video === void 0 ? void 0 : video.current) === null || _a === void 0 ? void 0 : _a.currentTime,
                };
                var timeAlreadyView = getTimeViewed(joinTimeRanges(__spreadArray(__spreadArray([], videoPlayedRange, true), [currentRange], false)));
                setTimeViewed(timeAlreadyView);
            };
            var intervalEvent_1 = setInterval(readTime, FRAME_INTERVAL);
            var checkViewedTimeEvent_1 = setInterval(checkTimeViewed, 30);
            return function () {
                clearInterval(intervalEvent_1);
                clearInterval(checkViewedTimeEvent_1);
            };
        }
    }, [video, isEnded, isPlaying, timeViewed, videoPlayedRange, startRangePlayed]);
    // Reset playing state if Video.current ended
    var handleEnded = function () {
        var _a;
        if (video.current) {
            var currentRange = {
                start: startRangePlayed,
                end: (_a = video === null || video === void 0 ? void 0 : video.current) === null || _a === void 0 ? void 0 : _a.duration,
            };
            setIsEnded(true);
            setTimeRate(100);
            setIsPlaying(false);
            setVideoPlayedRange(__spreadArray(__spreadArray([], videoPlayedRange, true), [currentRange], false));
            setStartRangePlayed(0);
        }
    };
    // Play or pause
    useEffectOnExistingElement(function () { return (isPlaying ? video.current.play() : video.current.pause()); }, video, [isPlaying]);
    useEffectOnExistingElement(function () { return (video.current.playbackRate = speed); }, video, [speed]);
    var handleDownload = function () {
        var _a, _b;
        if (!((_a = video.current) === null || _a === void 0 ? void 0 : _a.buffered) || video.current.buffered.length === 0) {
            return;
        }
        var bufferedEnd = video.current.buffered.end(video.current.buffered.length - 1);
        if (((_b = video.current) === null || _b === void 0 ? void 0 : _b.duration) && !Number.isNaN(video.current.duration) && video.current.duration > 0) {
            setDownloadRate((bufferedEnd / video.current.duration) * 100);
        }
    };
    var handlePlay = function () {
        var newPlaying = !isPlaying;
        if (newPlaying) {
            setStartRangePlayed(Math.max(video.current.currentTime - ERROR_MARGE_FOR_VIEW_RANGE, 0));
        }
        else {
            var newRange = {
                start: startRangePlayed,
                end: video.current.currentTime,
            };
            setVideoPlayedRange(joinTimeRanges(__spreadArray(__spreadArray([], videoPlayedRange, true), [newRange], false)));
        }
        setIsPlaying(newPlaying);
        setIsEnded(false);
    };
    var handleSpeedChange = function () {
        var currentIndex = PLAYBACK_SPEEDS.findIndex(function (playbackSpeed) { return playbackSpeed === speed; });
        var isSpeedNotFound = currentIndex === -1;
        if (isSpeedNotFound) {
            setSpeed(DEFAULT_PLAYBACK_SPEED);
            return;
        }
        var nextIndex = (currentIndex + 1) % PLAYBACK_SPEEDS.length;
        var nextSpeed = PLAYBACK_SPEEDS[nextIndex];
        setSpeed(nextSpeed);
    };
    var handleTimeChange = function (newTime, oldTime) {
        var _a;
        if (isPlaying) {
            var newRange = { start: startRangePlayed, end: oldTime };
            setVideoPlayedRange(joinTimeRanges(__spreadArray(__spreadArray([], videoPlayedRange, true), [newRange], false)));
            setStartRangePlayed(newTime);
        }
        video.current.currentTime = newTime;
        setIsEnded(false);
        if (((_a = video.current) === null || _a === void 0 ? void 0 : _a.duration) && !Number.isNaN(video.current.duration) && video.current.duration > 0) {
            setTimeRate((newTime / video.current.duration) * 100);
        }
    };
    return {
        currentTime: ((_a = video === null || video === void 0 ? void 0 : video.current) === null || _a === void 0 ? void 0 : _a.currentTime) || 0,
        duration: ((_b = video === null || video === void 0 ? void 0 : video.current) === null || _b === void 0 ? void 0 : _b.duration) || 0,
        timeViewed: timeViewed,
        downloadRate: downloadRate,
        isPlaying: isPlaying,
        isEnded: isEnded,
        speed: speed,
        timeRate: timeRate,
        videoPlayedRange: videoPlayedRange,
        onDownload: handleDownload,
        onPlay: handlePlay,
        onEnded: handleEnded,
        onSpeedChange: handleSpeedChange,
        onTimeChange: handleTimeChange,
    };
};
